import React, { useEffect, useImperativeHandle, useRef, forwardRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

/**
 * Asistente paso a paso para configurar una integracion con un sitio externo (ARCA, MercadoPago...).
 *
 * Portado de `shared/integracion-stepper` de tienda-admin (13b5a5b). Cada paso junta en un mismo lugar la
 * ayuda de que hacer afuera, con capturas, y lo que hay que completar aca (`acciones[paso.id]`). El que lo
 * usa define los pasos y las acciones; el stepper solo navega.
 *
 * Un paso: { id, titulo, ayuda (admite <strong>, <code>, <em>), capturas: [{src, texto}], link: {url, texto},
 * bloqueo: () => texto | null }. Si `bloqueo` devuelve texto no se puede pasar al siguiente.
 *
 * Con `storageKey` recuerda en que paso quedo el usuario: el tramite obliga a ir y venir del sitio externo y
 * es facil recargar la pagina en el medio. Es una comodidad por navegador, no un dato que importe perder.
 */
export const IntegracionStepper = forwardRef(({ pasos, basePath, paso, onPaso, acciones, storageKey }, ref) => {
    const topRef = useRef(null);

    /** Hasta que paso se puede llegar: el primero bloqueado (incluido, para poder resolverlo). */
    const maxAlcanzable = () => {
        const i = pasos.findIndex((p) => p.bloqueo && !!p.bloqueo());
        return i === -1 ? pasos.length - 1 : i;
    };

    const guardar = (i) => {
        if (!storageKey) return;
        try {
            localStorage.setItem("stepper:" + storageKey, String(i));
        } catch (e) { /* sin storage, se pierde el paso al recargar: no pasa nada */ }
    };

    const irA = (i, persistir = true) => {
        if (i < 0 || i >= pasos.length || i > maxAlcanzable()) return;
        onPaso(i);
        if (persistir) guardar(i);
        // El paso nuevo arranca arriba: si no, el usuario queda mirando el pie del anterior.
        setTimeout(() => topRef.current && topRef.current.scrollIntoView({ block: "nearest" }));
    };

    const actual = pasos[paso];
    const bloqueoActual = actual && actual.bloqueo ? actual.bloqueo() : null;

    useImperativeHandle(ref, () => ({
        siguiente: () => irA(paso + 1),
        irAPaso: (id) => irA(pasos.findIndex((p) => p.id === id)),
    }));

    // localStorage puede no estar (modo privado, datos bloqueados): todo envuelto, y sin el funciona igual.
    useEffect(() => {
        if (!storageKey) return;
        try {
            const v = localStorage.getItem("stepper:" + storageKey);
            if (v !== null && Number(v) < pasos.length) irA(Number(v), false);
        } catch (e) { /* nada */ }
        // Solo al montar, igual que ngAfterContentInit.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const maximo = maxAlcanzable();
    const accion = actual && acciones ? acciones[actual.id] : null;

    return (
        <div ref={topRef} className="stepper">
            {/* Progreso: un chip por paso. Se puede volver a cualquiera y avanzar hasta el primero pendiente. */}
            <ol className="stepper__progreso">
                {pasos.map((p, i) => (
                    <li key={p.id}>
                        <button type="button" onClick={() => irA(i)} disabled={i > maximo}>
                            <div className={i <= paso ? "stepper__barra stepper__barra--hecha" : "stepper__barra"} />
                            <div className={"stepper__chip" + (i === paso ? " stepper__chip--actual" : "") + (i > maximo ? " stepper__chip--bloqueado" : "")}>
                                <span>{i + 1}.</span>
                                <span className="stepper__chip-titulo">{p.titulo}</span>
                            </div>
                        </button>
                    </li>
                ))}
            </ol>

            {actual && (
                <>
                    <div>
                        <p className="cuenta-usuarios__eyebrow">PASO {paso + 1} DE {pasos.length}</p>
                        <h4 className="stepper__titulo">{actual.titulo}</h4>
                    </div>

                    {/* Que hacer afuera */}
                    {(actual.ayuda || actual.link) && (
                        <div className="stepper__ayuda">
                            {actual.ayuda && <p dangerouslySetInnerHTML={{ __html: actual.ayuda }} />}
                            {actual.link && (
                                <a href={actual.link.url} target="_blank" rel="noopener noreferrer" className="button" data-type="secondary">
                                    {actual.link.texto} <FontAwesomeIcon icon="fa-solid fa-arrow-up-right-from-square" />
                                </a>
                            )}
                        </div>
                    )}

                    {/* Capturas: chicas aca, a tamaño real en otra pestaña */}
                    {actual.capturas && actual.capturas.length > 0 && (
                        <div className={actual.capturas.length > 1 ? "stepper__capturas stepper__capturas--grilla" : "stepper__capturas"}>
                            {actual.capturas.map((c, ci) => (
                                <figure key={c.src}>
                                    <a href={basePath + c.src} target="_blank" rel="noopener noreferrer">
                                        <img src={basePath + c.src} alt={c.texto || actual.titulo} loading="lazy" />
                                    </a>
                                    {c.texto && (
                                        <figcaption>
                                            {actual.capturas.length > 1 && <strong>{ci + 1}.</strong>} {c.texto}
                                        </figcaption>
                                    )}
                                </figure>
                            ))}
                        </div>
                    )}

                    {/* Que hacer aca, en este mismo paso */}
                    {accion && (
                        <div className="stepper__accion">
                            <p className="stepper__accion-titulo">
                                <FontAwesomeIcon icon="fa-solid fa-pen-to-square" /> COMPLETÁ ACÁ
                            </p>
                            {accion}
                        </div>
                    )}

                    {/* Navegacion */}
                    <div className="stepper__nav">
                        <button type="button" className="stepper__atras" onClick={() => irA(paso - 1)} disabled={paso === 0}>
                            <FontAwesomeIcon icon="fa-solid fa-arrow-left" /> Atrás
                        </button>
                        <div className="stepper__nav-derecha">
                            {bloqueoActual && (
                                <span className="stepper__bloqueo">
                                    <FontAwesomeIcon icon="fa-solid fa-circle-info" /> {bloqueoActual}
                                </span>
                            )}
                            {paso < pasos.length - 1 && (
                                <button type="button" className="button" data-type="accent" onClick={() => irA(paso + 1)} disabled={!!bloqueoActual}>
                                    Siguiente <FontAwesomeIcon icon="fa-solid fa-arrow-right" />
                                </button>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
});
